'use client';
import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

interface NotificationContextValue {
  unreadCount: number;
  refreshUnread: () => void;
}

const NotificationContext = createContext<NotificationContextValue>({
  unreadCount: 0,
  refreshUnread: () => {},
});

const TYPE_TOAST_ICONS: Record<string, string> = {
  new_order: '🛍️',
  approval_status: '✅',
  pickup_assigned: '📦',
  order_update: '🚚',
  payment: '💳',
  delivery_assigned: '🚴',
  delivery_completed: '🎉',
  order_status: '📋',
  kyc_approval: '🪪',
  inventory_warning: '⚠️',
  system: '🔔',
};

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [userId, setUserId] = useState<string | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const fetchUnread = useCallback(async (uid: string) => {
    const { count } = await supabase
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', uid)
      .eq('is_read', false);
    setUnreadCount(count ?? 0);
  }, []);

  const refreshUnread = useCallback(() => {
    if (userId) fetchUnread(userId);
  }, [userId, fetchUnread]);

  useEffect(() => {
    let mounted = true;

    async function init() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !mounted) return;
      setUserId(user.id);
      await fetchUnread(user.id);

      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }

      channelRef.current = supabase
        .channel(`notif-badge-${user.id}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        }, (payload) => {
          if (!mounted) return;
          setUnreadCount(prev => prev + 1);

          // Show toast for new notification
          const notif = payload.new as {
            title?: string;
            body?: string;
            notification_type?: string;
          };
          if (notif?.title) {
            const icon = TYPE_TOAST_ICONS[notif.notification_type || 'system'] || '🔔';
            toast(notif.title, {
              description: notif.body || undefined,
              icon,
              duration: 5000,
            });
          }
        })
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        }, () => {
          if (mounted) fetchUnread(user.id);
        })
        .subscribe();
    }

    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setUnreadCount(0);
        setUserId(null);
        if (channelRef.current) {
          supabase.removeChannel(channelRef.current);
          channelRef.current = null;
        }
      } else if (event === 'SIGNED_IN' && session?.user) {
        setUserId(session.user.id);
        fetchUnread(session.user.id);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [fetchUnread]);

  return (
    <NotificationContext.Provider value={{ unreadCount, refreshUnread }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}
